import Typo from "@/src/components/atom/Typo";
import Flex from "@/src/components/molecule/Flex";
import TabItem from "@/src/components/molecule/TabItem";
import ExporterMainLayout from "@/src/components/template/Layout/exporter/ExporterMainLayout";
import colorSet from "@/src/styles/color";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled, { css } from "styled-components";
import UsageHistory from "./components/UsageHistory";
import PurchaseRefund from "./components/PurchaseRefund";
import ExpirationScheduled from "./components/ExpirationScheduled";
import { useTranslation } from "react-i18next";
import { CreditContextProvider } from "./context/creditContext";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import CreditInfo from "./components/CreditInfo";
import { useGetSessionQuery } from "@/src/store/apis/auth";
import {
  useGetPaymentMethodsQuery,
  useGetPaymentsQuery,
  useLazyGetCurrentSubscriptionQuery,
} from "@/src/store/apis/subscription";
import CreditCallout from "./components/CreditCallout";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import { useGetCreditStatusInfoQuery } from "@/src/store/apis/credit";
import { PulseLoader } from "react-spinners";

export enum CreditSelectTabState {
  // 사용 내역
  USAGE_HISTORY = "USAGE_HISTORY",
  // 구매 환불 내역
  PURCHASE_REFUND = "PURCHASE_REFUND",
  // 소멸 예정 내역
  EXPIRATION_SCHEDULED = "EXPIRATION_SCHEDULED",
}

export const TAB_LIST: CreditSelectTabState[] = [
  CreditSelectTabState.USAGE_HISTORY,
  CreditSelectTabState.PURCHASE_REFUND,
  CreditSelectTabState.EXPIRATION_SCHEDULED,
];

function ExporterCreditPage() {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathRef = useRef(location.pathname);
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectTab, setSelectTab] = useState<CreditSelectTabState>(
    (searchParams.get("selectTab") as unknown as CreditSelectTabState) ??
      CreditSelectTabState.USAGE_HISTORY
  );

  // API
  const [getSubscription] = useLazyGetCurrentSubscriptionQuery();
  useGetPaymentsQuery(
    {
      page: 1,
      pageSize: 1,
      paymentType: "CREDIT",
    },
    { refetchOnMountOrArgChange: true }
  );
  const { isFetching: isCreditStatusInfoFetching } =
    useGetCreditStatusInfoQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });
  useGetSessionQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  useGetCommonCodeViaCodeNameQuery(
    {
      codeName: "COUNTRY",
    },
    { refetchOnMountOrArgChange: true }
  );
  useGetPaymentMethodsQuery(
    {
      page: 1,
      pageSize: 2,
    },
    { refetchOnMountOrArgChange: true }
  );

  const mountWhenFetching = useCallback(async () => {
    await getSubscription().unwrap();
  }, [getSubscription]);

  const handleTabChange = (newTab: string) => {
    if (newTab === selectTab) return;

    setSelectTab(newTab as CreditSelectTabState);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("selectTab", newTab);
    setSearchParams(newParams, { replace: true });
  };

  const renderTabItemTitle = (type: CreditSelectTabState) => {
    switch (type) {
      case CreditSelectTabState.USAGE_HISTORY:
        return t("common:usageHistory");
      case CreditSelectTabState.PURCHASE_REFUND:
        return t("common:purchaseRefundHistory");
      case CreditSelectTabState.EXPIRATION_SCHEDULED:
        return t("common:expirationSchedule");
    }
  };

  const renderTabItemContent = (tabType: CreditSelectTabState) => {
    switch (tabType) {
      case CreditSelectTabState.USAGE_HISTORY:
        return <UsageHistory />;
      case CreditSelectTabState.PURCHASE_REFUND:
        return <PurchaseRefund />;
      case CreditSelectTabState.EXPIRATION_SCHEDULED:
        return <ExpirationScheduled />;
    }
  };

  const renderContent = () => {
    if (isCreditStatusInfoFetching) {
      return (
        <LoaderContainer justifyContent="center" alignItems="center">
          <PulseLoader color={colorSet.blue4} />
        </LoaderContainer>
      );
    }

    return (
      <>
        <CreditCallout />
        <CreditContextProvider>
          <Flex flexDirection="column" gap={32}>
            {/* Credit Info */}
            <CreditInfo onTabChange={setSelectTab} />
            {/* Table Content */}
            <Flex flexDirection="column" gap={16}>
              <Tabs role="tablist">
                {TAB_LIST.map((item, idx) => {
                  return (
                    <StyledTabItem
                      key={idx.toString()}
                      ref={item === selectTab ? buttonRef : null}
                      tabIndex={item === selectTab ? 0 : -1}
                      isSelect={item === selectTab}
                      tabValue={item}
                      onClick={() => handleTabChange(item)}
                      onFocusItem={handleTabChange}
                    >
                      <Typo
                        typoType="b5m"
                        color={item === selectTab ? "white" : "gray6"}
                      >
                        {renderTabItemTitle(item)}
                      </Typo>
                    </StyledTabItem>
                  );
                })}
              </Tabs>
              {selectTab && renderTabItemContent(selectTab)}
            </Flex>
          </Flex>
        </CreditContextProvider>
      </>
    );
  };

  useEffect(() => {
    if (
      prevPathRef.current !== location.pathname &&
      location.pathname === EXPORTER_PRIVATE_PATH.CREDIT
    ) {
      navigate(`/export/credit?selectTab=${selectTab}`, { replace: true });
    }

    prevPathRef.current = location.pathname;
  }, [location.pathname, navigate, selectTab]);

  useEffect(() => {
    mountWhenFetching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ExporterMainLayout
      breadcrumb={[t("common:exportCredit")]}
      pageTitle={t("common:exportCredit")}
    >
      {renderContent()}
    </ExporterMainLayout>
  );
}

export default ExporterCreditPage;

const Tabs = styled.div`
  display: flex;
  border-radius: 8px;
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.05);
`;

const StyledTabItem = styled(TabItem)<{
  isSelect?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: 8px 16px;
  cursor: pointer;
  background: ${({ isSelect }) =>
    isSelect ? colorSet.indigo : colorSet.gray11};

  &:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  ${({ isSelect }) => {
    return isSelect
      ? css`
          border: none;
        `
      : css`
          border: 1px solid ${colorSet.gray9};
        `;
  }}
`;

const LoaderContainer = styled(Flex)`
  height: 100%;
`;
